import { useCallback } from 'react';
import { useAtom } from 'jotai';
import { notificationsAtom, type AppNotification } from './atoms';

/**
 * Encapsulates the notification bar state: reading the current list, pushing
 * new entries, dismissing by ID, and clearing all at once.
 *
 * Notifications can optionally carry a `source` key identifying the workflow
 * that produced them (e.g. `'dps-schedule'`, `'save'`). Source-tagged pushes
 * deduplicate within their source — pushing twice from the same source keeps
 * only the latest message rather than piling errors. Workflows can call
 * `clearNotificationsBySource` from their success or retry path to wipe
 * stale errors without disturbing notifications from unrelated workflows.
 *
 * @param {number} packageId - The package ID (atom family key).
 * @return {object} Notification list and mutation callbacks.
 */
export const useNotifications = (
  packageId: number
): {
  notifications: AppNotification[];
  pushNotification: (message: string, source?: string) => void;
  dismissNotification: (id: string) => void;
  clearNotifications: () => void;
  clearNotificationsBySource: (source: string) => void;
} => {
  const [notifications, setNotifications] = useAtom(
    notificationsAtom(packageId)
  );

  /**
   * Appends an error notification to the notification bar. When `source` is
   * supplied, any existing notifications with the same source are removed
   * first so the bar shows only the latest error from that workflow.
   *
   * @param {string} message - The error message to display.
   * @param {string} [source] - Optional workflow key for dedup/clearing.
   */
  const pushNotification = useCallback(
    (message: string, source?: string) => {
      const entry: AppNotification = {
        id: crypto.randomUUID(),
        message,
        source,
      };
      setNotifications((prev) => {
        const filtered = source
          ? prev.filter((n) => n.source !== source)
          : prev;
        return [...filtered, entry];
      });
    },
    [setNotifications]
  );

  /**
   * Removes a single notification by ID (user dismissed it).
   *
   * @param {string} id - The notification ID to remove.
   */
  const dismissNotification = useCallback(
    (id: string) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [setNotifications]
  );

  /**
   * Clears all notifications regardless of source. Use sparingly — prefer
   * `clearNotificationsBySource` so an unrelated workflow's errors aren't
   * silently swept away.
   */
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, [setNotifications]);

  /**
   * Removes all notifications belonging to the given source. Call from a
   * workflow's success path so stale errors from prior failed attempts of
   * the same workflow disappear once the latest attempt succeeds.
   *
   * @param {string} source - The source key to clear (e.g. `'dps-schedule'`).
   */
  const clearNotificationsBySource = useCallback(
    (source: string) => {
      setNotifications((prev) => prev.filter((n) => n.source !== source));
    },
    [setNotifications]
  );

  return {
    notifications,
    pushNotification,
    dismissNotification,
    clearNotifications,
    clearNotificationsBySource,
  };
};
